// Wrangler's condition trees (its DESIGN.md section 7) in plain English.
//
//   {"op": "and" | "or", "children": [...]}                   a group: one connector for all children
//   {"question_id": 2, "operator": "in", "values": [10001]}   a leaf: choice questions use option IDs
//   {"match": "all"}                                          the overall quota (Wrangler v1.3.0)
//
// describe() returns parts for display: plain strings, {question} for a question's name, and
// {connector} for AND / OR, so the summary can be styled; summaryText() joins them into a string.
// Every quota, rule, import and preview in Ranger's Wrangler screens is summarised by this.

/** Alchemer's titles hold HTML (e.g. <strong>): the text alone, on one line. */
export const stripTags = (text) => String(text ?? '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

/** Questions by ID, each with a display label; a grid row is "<grid>: <row>". */
export const indexQuestions = (questions = []) => {
  const byId = new Map(questions.map((q) => [q.question_id, q]));
  const index = new Map();
  for (const q of questions) {
    const parent = q.parent_question_id != null ? byId.get(q.parent_question_id) : null;
    const label = parent ? `${stripTags(parent.title)}: ${stripTags(q.title)}` : stripTags(q.title);
    index.set(q.question_id, { ...q, label });
  }
  return index;
};

const listOf = (items, word = 'or') => {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} ${word} ${items[items.length - 1]}`;
};

const valueLabels = (question, values = []) => values.map((v) => {
  const option = question?.options?.find((o) => o.option_id === v);
  if (option) return stripTags(option.title) || option.reporting_value;
  if (question?.options?.length) return `option ${v}`; // not in the synced list
  return typeof v === 'string' ? `“${v}”` : String(v);
});

const isLeaf = (node) => node && typeof node === 'object' && 'question_id' in node;
const isGroup = (node) => node && typeof node === 'object' && Array.isArray(node.children);

const ref = (index, id) => {
  const q = index.get(id);
  return { question: q ? q.label : `question ${id}`, missing: !q };
};

const leaf = (node, index) => {
  const question = index.get(node.question_id);
  const name = ref(index, node.question_id);
  const values = valueLabels(question, node.values);
  const [a, b] = values;
  const multi = question?.kind === 'multi-select';
  switch (node.operator) {
    case 'in':
      return multi ? ['ticked ', listOf(values), ' at ', name] : ['answered ', name, ' with ', listOf(values)];
    case 'not_in':
      // Wrangler's meaning: answered, and not one of these. A blank answer doesn't count.
      return multi
        ? ['answered ', name, ' without ticking ', listOf(values)]
        : ['answered ', name, ' with something other than ', listOf(values)];
    case 'answered': return ['answered ', name];
    case 'not_answered': return ["didn't answer ", name];
    case 'eq': return ['answered ', name, ' with exactly ', a];
    case 'gt': return ['answered ', name, ' with more than ', a];
    case 'gte': return ['answered ', name, ' with ', a, ' or more'];
    case 'lt': return ['answered ', name, ' with less than ', a];
    case 'lte': return ['answered ', name, ' with ', a, ' or less'];
    case 'between': return ['answered ', name, ' with ', a, ' to ', b];
    default: return ['meet a condition on ', name, ` (${node.operator})`];
  }
};

/**
 * (not_in OR not_answered) on one question: "not one of these, or skipped it", the way Alchemer's
 * "is not one of" counts. Wrangler v1.4.0 imports it as plain not_in; the import review's skip
 * question (skips.js) changes it to this.
 */
const importedNotOneOf = (node, index) => {
  if (node.op !== 'or' || node.children.length !== 2 || !node.children.every(isLeaf)) return null;
  const notIn = node.children.find((c) => c.operator === 'not_in');
  const blank = node.children.find((c) => c.operator === 'not_answered');
  if (!notIn || !blank || notIn.question_id !== blank.question_id) return null;
  const question = index.get(notIn.question_id);
  const name = ref(index, notIn.question_id);
  const values = listOf(valueLabels(question, notIn.values));
  return question?.kind === 'multi-select'
    ? ['ticked none of ', values, ' at ', name, ', or skipped it']
    : ['chose something other than ', values, ' for ', name, ', or skipped it'];
};

const describeNode = (node, index, depth, joinUnknown) => {
  if (node?.match === 'all') return ['are any complete'];
  if (isLeaf(node)) return leaf(node, index);
  if (!isGroup(node) || node.children.length === 0) return ['(an empty group)'];
  const imported = importedNotOneOf(node, index);
  if (imported) return imported;
  if (node.children.length === 1) return describeNode(node.children[0], index, depth, false);
  // An imported quota whose join Alchemer didn't reveal: its top-level groups wait for a choice.
  const connector = depth === 0 && joinUnknown ? 'unknown' : node.op;
  const parts = [];
  node.children.forEach((child, i) => {
    if (i > 0) parts.push({ connector });
    parts.push(...describeNode(child, index, depth + 1, false));
  });
  return depth > 0 ? ['(', ...parts, ')'] : parts;
};

/** "Counts people who answered “Region” with London AND …." as display parts. */
export const describe = (logic, questions, { joinUnknown = false, subject = 'Counts people who' } = {}) => {
  const index = questions instanceof Map ? questions : indexQuestions(questions);
  if (logic?.match === 'all') return ['Counts every complete.'];
  return [`${subject} `, ...describeNode(logic, index, 0, joinUnknown), '.'];
};

const CONNECTOR_TEXT = { and: 'AND', or: 'OR', unknown: 'AND/OR (not chosen yet)' };

export const connectorText = (connector) => CONNECTOR_TEXT[connector] ?? String(connector).toUpperCase();

/** The summary as one string, with question names in quotes. */
export const summaryText = (logic, questions, options) => describe(logic, questions, options)
  .map((part) => {
    if (typeof part === 'string' || typeof part === 'number') return String(part);
    if (part.connector) return ` ${connectorText(part.connector)} `;
    return `“${part.question}”`;
  })
  .join('');

/** Every question ID a tree uses. */
export const questionIds = (node, found = new Set()) => {
  if (isLeaf(node)) found.add(node.question_id);
  else if (isGroup(node)) node.children.forEach((c) => questionIds(c, found));
  return found;
};
